/**
 * Typography Components - プロフェッショナルデザインシステム
 * 視覚階層・可読性・アクセシビリティ完全対応
 */

import React, { forwardRef } from 'react';
import { tokens } from '../tokens';

// 見出しコンポーネント
const Heading = forwardRef(({
  children,
  level = 1,
  size,
  weight = 'bold',
  color = 'primary',
  align = 'left',
  className = '',
  ...props
}, ref) => {
  const Tag = `h${level}`;
  
  // サイズマッピング（levelベースまたはsize prop）
  const sizeMappings = {
    1: tokens.typography.fontSize['4xl'],
    2: tokens.typography.fontSize['3xl'],
    3: tokens.typography.fontSize['2xl'],
    4: tokens.typography.fontSize.xl,
    5: tokens.typography.fontSize.lg,
    6: tokens.typography.fontSize.base,
  };
  
  // カラーマッピング
  const colorMappings = {
    primary: tokens.colors.text.primary,
    secondary: tokens.colors.text.secondary,
    tertiary: tokens.colors.text.tertiary,
    inverse: tokens.colors.text.inverse,
  };
  
  const headingStyles = {
    margin: '0',
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: size || sizeMappings[level],
    fontWeight: tokens.typography.fontWeight[weight],
    lineHeight: tokens.typography.lineHeight.tight,
    color: colorMappings[color],
    textAlign: align,
    letterSpacing: level <= 2 ? tokens.typography.letterSpacing.tight : tokens.typography.letterSpacing.normal,
  };
  
  return (
    <Tag
      ref={ref}
      style={headingStyles}
      className={className}
      {...props}
    >
      {children}
    </Tag>
  );
});

Heading.displayName = 'Heading';

// テキストコンポーネント
const Text = forwardRef(({
  children,
  as = 'p',
  size = 'base',
  weight = 'normal',
  color = 'primary',
  align = 'left',
  leading = 'normal',
  className = '',
  ...props
}, ref) => {
  const Tag = as;
  
  const colorMappings = {
    primary: tokens.colors.text.primary,
    secondary: tokens.colors.text.secondary,
    tertiary: tokens.colors.text.tertiary,
    disabled: tokens.colors.text.disabled,
    inverse: tokens.colors.text.inverse,
  };
  
  const textStyles = {
    margin: '0',
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: tokens.typography.fontSize[size],
    fontWeight: tokens.typography.fontWeight[weight],
    lineHeight: tokens.typography.lineHeight[leading],
    color: colorMappings[color],
    textAlign: align,
    letterSpacing: tokens.typography.letterSpacing.normal,
  };
  
  return (
    <Tag
      ref={ref}
      style={textStyles}
      className={className}
      {...props}
    >
      {children}
    </Tag>
  );
});

Text.displayName = 'Text';

// コードテキストコンポーネント
const Code = forwardRef(({
  children,
  size = 'sm',
  inline = true,
  className = '',
  ...props
}, ref) => {
  const Tag = inline ? 'code' : 'pre';
  
  const codeStyles = {
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: tokens.typography.fontSize[size],
    fontWeight: tokens.typography.fontWeight.normal,
    lineHeight: tokens.typography.lineHeight.normal,
    color: tokens.colors.text.primary,
    backgroundColor: tokens.colors.gray[100],
    padding: inline ? `${tokens.spacing[1]} ${tokens.spacing[2]}` : tokens.spacing[4],
    borderRadius: tokens.borders.radius.sm,
    border: `${tokens.borders.width[1]} solid ${tokens.colors.gray[200]}`,
    ...(inline ? {
      display: 'inline',
    } : {
      display: 'block',
      overflow: 'auto',
      whiteSpace: 'pre',
    }),
  };
  
  return (
    <Tag
      ref={ref}
      style={codeStyles}
      className={className}
      {...props}
    >
      {children}
    </Tag>
  );
});

Code.displayName = 'Code';

// 強調テキストコンポーネント
const Strong = forwardRef(({
  children,
  className = '',
  ...props
}, ref) => {
  const strongStyles = {
    fontWeight: tokens.typography.fontWeight.semibold,
    color: 'inherit',
  };
  
  return (
    <strong
      ref={ref}
      style={strongStyles}
      className={className}
      {...props}
    >
      {children}
    </strong>
  );
});

Strong.displayName = 'Strong';

// リンクコンポーネント
const Link = forwardRef(({
  children,
  href,
  external = false,
  underline = true,
  color = 'primary',
  className = '',
  onClick,
  ...props
}, ref) => {
  const colorMappings = {
    primary: tokens.colors.primary[500],
    secondary: tokens.colors.text.secondary,
    tertiary: tokens.colors.text.tertiary,
  };
  
  const linkStyles = {
    color: colorMappings[color],
    textDecoration: underline ? 'underline' : 'none',
    textUnderlineOffset: '2px',
    textDecorationThickness: '1px',
    cursor: 'pointer',
    transition: `color ${tokens.transitions.duration.fast} ${tokens.transitions.timing.ease}`,
    outline: 'none',
    
    '&:hover': {
      color: tokens.colors.primary[600],
      textDecorationThickness: '2px',
    },
    
    '&:focus-visible': {
      outline: `2px solid ${tokens.colors.primary[500]}`,
      outlineOffset: '2px',
      borderRadius: tokens.borders.radius.sm,
    },
    
    '&:active': {
      color: tokens.colors.primary[700],
    },
  };
  
  const externalProps = external ? {
    target: '_blank',
    rel: 'noopener noreferrer',
  } : {};
  
  return (
    <a
      ref={ref}
      href={href}
      style={linkStyles}
      className={className}
      onClick={onClick}
      {...externalProps}
      {...props}
    >
      {children}
      {external && (
        <span style={{ marginLeft: tokens.spacing[1] }}>
          ↗
        </span>
      )}
    </a>
  );
});

Link.displayName = 'Link';

// リストコンポーネント
const List = forwardRef(({
  children,
  ordered = false,
  spacing = 'base',
  className = '',
  ...props
}, ref) => {
  const Tag = ordered ? 'ol' : 'ul';
  
  const listStyles = {
    margin: '0',
    padding: `0 0 0 ${tokens.spacing[6]}`,
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: tokens.typography.fontSize.base,
    lineHeight: tokens.typography.lineHeight.normal,
    color: tokens.colors.text.primary,
    
    '& li': {
      marginBottom: tokens.spacing[spacing === 'compact' ? 1 : 2],
    },
    
    '& li:last-child': {
      marginBottom: '0',
    },
  };
  
  return (
    <Tag
      ref={ref}
      style={listStyles}
      className={className}
      {...props}
    >
      {children}
    </Tag>
  );
});

List.displayName = 'List';

const ListItem = forwardRef(({
  children,
  className = '',
  ...props
}, ref) => {
  return (
    <li
      ref={ref}
      className={className}
      {...props}
    >
      {children}
    </li>
  );
});

ListItem.displayName = 'ListItem';

// 引用コンポーネント
const Blockquote = forwardRef(({
  children,
  cite,
  className = '',
  ...props
}, ref) => {
  const blockquoteStyles = {
    margin: `${tokens.spacing[6]} 0`,
    padding: `${tokens.spacing[4]} ${tokens.spacing[6]}`,
    borderLeft: `4px solid ${tokens.colors.primary[500]}`,
    backgroundColor: tokens.colors.gray[50],
    fontStyle: 'italic',
    fontSize: tokens.typography.fontSize.lg,
    lineHeight: tokens.typography.lineHeight.relaxed,
    color: tokens.colors.text.secondary,
  };
  
  return (
    <blockquote
      ref={ref}
      style={blockquoteStyles}
      className={className}
      cite={cite}
      {...props}
    >
      {children}
    </blockquote>
  );
});

Blockquote.displayName = 'Blockquote';

export {
  Heading,
  Text,
  Code,
  Strong,
  Link,
  List,
  ListItem,
  Blockquote,
};